///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { GtthHistoryService } from '../../logical-topology/engine/history-service';
import { GtthBomReportService } from './bom-report.service';
import { Subscription } from 'rxjs';
import { FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { GtthInputControlService } from '../../../shared/reactive-forms/input-control.service';
import { GtthReactiveDropdown } from '../../../shared/reactive-forms/input-dropdown';
import { GtthReactiveInputBase } from '../../../shared/reactive-forms/input-base';
import { GtthReactiveCheckbox } from '../../../shared/reactive-forms/input-checkbox';
import { GtthKeyValueDTO, ProductAccessoryTypeEnum, ProductDeviceTypeEnum } from '../../../generated/shared-types.model';
import { ExtendedBomDeviceDTO } from './dto/extended-bom-device-dto.model';

import { ProjectProductsService } from '../../logical-topology/engine/project-products-service';
import { GtthProjectInfoViewService } from '../../../layouts/navbar/project-info-view.service';
import { E2eTestUtils } from '../../../../testing/e2e-test-utils';
import { GtthReactiveNumber } from '../../../shared/reactive-forms/input-number';
import { isDefined } from '@siklu/sui-lib/utils';
import { SuiSvgIconComponent, SuiTooltipDirective } from '@siklu/sui-lib';
import { NgClass } from '@angular/common';
import { ContextSensitiveHelpComponent } from '../../../shared/context-sensitive-help/ctx-sensitive-help.component';
import { GtthReactiveFormsModule } from '../../../shared/reactive-forms/reactie-forms.module';
import { GtthStyleDirectivesModule } from '../../../shared/directives/style-directives/style-directives.module';
import { ButtonsDirectivesModule } from '../../../shared/directives/buttons-directives/buttons-directives.module';
import { ActionIconDirectivesModule } from '../../../shared/directives/action-icon-directives/action-icon-directives.module';
import { GtthDisableFormControlsDirective } from '../../../shared/directives/validation-directives/disable-form-controls.directive';

@Component({
    selector: 'winde-bom-device-setings',
    templateUrl: './bom-device-settings.component.html',
    styleUrls: ['./bom-report.scss'],
    imports: [
        ReactiveFormsModule,
        NgClass,
        SuiSvgIconComponent,
        GtthReactiveFormsModule,
        ContextSensitiveHelpComponent,
        SuiTooltipDirective,
        GtthStyleDirectivesModule,
        ButtonsDirectivesModule,
        ActionIconDirectivesModule,
        GtthDisableFormControlsDirective
    ]
})
export class GtthBomDeviceSettingsComponent implements OnInit, OnDestroy {
    public readonly tooltipsAreDisabled = E2eTestUtils.isTooltipsDisabled();
    public showDevicesList = true;
    public devices: ExtendedBomDeviceDTO[];
    public devicesToShow: ExtendedBomDeviceDTO[];
    public activeDevice: ExtendedBomDeviceDTO;
    public activeDeviceAntennaName: string;
    public editForm: FormGroup;
    public configInputs: GtthReactiveInputBase<any>[];
    public activeLayerIndex: number;
    public layersObject: Record<number, string>;
    @Output() closeConfig: EventEmitter<void> = new EventEmitter<void>();
    private readonly SRG_QUANTIRY_ATTRIBUTE = 'srgQuantity';
    private activeDeviceIndex: number;
    private readonly configFieldsKeys = {
        antenna: { id: 'antennaAccessoryId', value: 'antennaName', type: ProductAccessoryTypeEnum.ANTENNA },
        MK: { id: 'mountingKitId', value: 'mountingKit', type: ProductAccessoryTypeEnum.MK },
        pse: { id: 'pseId', value: 'pse', type: ProductAccessoryTypeEnum.OPT_PSE },
        poe: { id: 'poeId', value: 'poe', type: ProductAccessoryTypeEnum.POWER_INJECTORS },
        srg: { id: 'srgId', value: 'srg', type: ProductAccessoryTypeEnum.SRG },
        sfp: { id: 'sfpId', value: 'sfp', type: ProductAccessoryTypeEnum.SFP },
        capacityLicense: {
            id: 'capacityLicenseId',
            value: 'capacityLicense',
            type: ProductAccessoryTypeEnum.SOFTWARE_UPGRADE
        },
        aes: { id: 'aesId', value: 'aes', type: ProductAccessoryTypeEnum.OPT_AES },
        l2: { id: 'l2Id', value: 'l2', type: ProductAccessoryTypeEnum.OPT_L2 },
        extendmm: { id: 'extendmmId', value: 'extendmm', type: ProductAccessoryTypeEnum.OPT_EXTENDMM },
        sync: { id: 'syncId', value: 'sync', type: ProductAccessoryTypeEnum.OPT_SYNC }
    };
    private readonly formControllClass = 'form-control-sm';
    private buDevices: ExtendedBomDeviceDTO[];
    private tuDevices: ExtendedBomDeviceDTO[];
    private fxParts: Map<number, Record<string, ExtendedBomDeviceDTO[]>>;
    private layerChangedSubscriber: Subscription;

    constructor(
        public bomReportService: GtthBomReportService,
        public historyService: GtthHistoryService,
        private readonly projectProductsService: ProjectProductsService,
        private readonly inputControlService: GtthInputControlService,
        private readonly projectInfoService: GtthProjectInfoViewService
    ) {}

    ngOnInit(): void {
        this.layersObject = this.projectInfoService.getLayersAsKeyValueObj();
        this.setActiveLayerIndex(this.historyService.logicalTopology.activeLayerIdx);
        this.loadDevicesConfigs();

        this.layerChangedSubscriber = this.projectInfoService.layerChangedSubscription().subscribe((layerIndex: number) => {
            this.layersObject = this.projectInfoService.getLayersAsKeyValueObj();
            this.setActiveLayerIndex(layerIndex);
            this.loadDevicesConfigs();
        });
    }

    ngOnDestroy() {
        this.saveDeviceConfig();
        if (this.layerChangedSubscriber) {
            this.layerChangedSubscriber.unsubscribe();
            this.layerChangedSubscriber = null;
        }
    }

    selectDevice(deviceIndex: number) {
        if (this.editForm && isDefined(this.activeDeviceIndex)) {
            this.parseBomFormToObj(this.devicesToShow[this.activeDeviceIndex], this.editForm);
        }
        this.activeDeviceIndex = deviceIndex;
        this.activeDevice = this.devicesToShow[this.activeDeviceIndex];
        this.activeDeviceAntennaName = this.projectProductsService.getAntennaByBaseId(this.activeDevice.antenna).name;
        this.initForm();
    }

    switchDevice(side: number) {
        const currentDeviceIndex = this.devicesToShow.findIndex((device: ExtendedBomDeviceDTO) => {
            return device.typeProductId === this.activeDevice.typeProductId && device.antenna === this.activeDevice.antenna;
        });

        let nextIndex = currentDeviceIndex + side;

        if (nextIndex >= this.devicesToShow.length) {
            nextIndex = 0;
        } else if (nextIndex < 0) {
            nextIndex = this.devicesToShow.length - 1;
        }

        this.selectDevice(nextIndex);
    }

    getFormControls(): GtthReactiveInputBase<any>[] {
        let inputs: GtthReactiveInputBase<any>[] = [
            new GtthReactiveDropdown({
                key: this.configFieldsKeys.antenna.id,
                value: this.activeDevice.antennaAccessoryId,
                label: 'Antenna',
                options: this.activeDevice.available[ProductAccessoryTypeEnum.ANTENNA],
                order: 0,
                inputClass: this.formControllClass,
                hideInput: !(
                    this.activeDevice.available[ProductAccessoryTypeEnum.ANTENNA].length > 1 && this.historyService.isEditModeEnabled
                )
            } as GtthReactiveDropdown),
            new GtthReactiveDropdown({
                key: this.configFieldsKeys.MK.id,
                value: this.activeDevice.mountingKitId,
                label: 'Mounting kit',
                options: this.activeDevice.available[ProductAccessoryTypeEnum.MK],
                order: 1,
                inputClass: this.formControllClass,
                hideInput: !(this.activeDevice.available[ProductAccessoryTypeEnum.MK].length > 1 && this.historyService.isEditModeEnabled)
            } as GtthReactiveDropdown),
            new GtthReactiveCheckbox({
                key: this.configFieldsKeys.pse.id,
                value: this.isChecked(this.activeDevice.pseId, this.activeDevice.available[ProductAccessoryTypeEnum.OPT_PSE]),
                label: 'PSE',
                order: 2,
                checkboxLabel: 'Enable',
                disabled: !this.historyService.isEditModeEnabled,
                hideInput: !(
                    this.activeDevice.available[ProductAccessoryTypeEnum.OPT_PSE].length > 1 && this.historyService.isEditModeEnabled
                ),
                displayValue: this.activeDevice.pse
            } as GtthReactiveCheckbox),
            new GtthReactiveDropdown({
                key: this.configFieldsKeys.poe.id,
                value: this.activeDevice.poeId,
                label: 'POE',
                options: this.activeDevice.available[ProductAccessoryTypeEnum.POWER_INJECTORS],
                order: 3,
                inputClass: this.formControllClass,
                hideInput: !(
                    this.activeDevice.available[ProductAccessoryTypeEnum.POWER_INJECTORS].length > 1 &&
                    this.historyService.isEditModeEnabled
                )
            } as GtthReactiveDropdown),
            new GtthReactiveDropdown({
                key: this.configFieldsKeys.srg.id,
                value: this.activeDevice.srgId,
                label: 'Surge Protector',
                options: this.activeDevice.available[ProductAccessoryTypeEnum.SRG],
                order: 3,
                inputClass: this.formControllClass,
                hideInput: !(this.activeDevice.available[ProductAccessoryTypeEnum.SRG].length > 1 && this.historyService.isEditModeEnabled)
            } as GtthReactiveDropdown),
            new GtthReactiveDropdown({
                key: this.configFieldsKeys.sfp.id,
                value: this.activeDevice.sfpId,
                label: 'SFP',
                options: this.activeDevice.available[ProductAccessoryTypeEnum.SFP],
                order: 4,
                inputClass: this.formControllClass,
                hideInput: !(this.activeDevice.available[ProductAccessoryTypeEnum.SFP].length > 1 && this.historyService.isEditModeEnabled)
            } as GtthReactiveDropdown),
            new GtthReactiveDropdown({
                key: this.configFieldsKeys.capacityLicense.id,
                value: this.activeDevice.capacityLicenseId,
                label: 'Capacity license',
                options: this.activeDevice.available[ProductAccessoryTypeEnum.SOFTWARE_UPGRADE],
                order: 5,
                inputClass: this.formControllClass,
                hideInput: !(
                    this.activeDevice.available[ProductAccessoryTypeEnum.SOFTWARE_UPGRADE].length > 1 &&
                    this.historyService.isEditModeEnabled
                )
            } as GtthReactiveDropdown)
        ];

        if (this.activeDevice.available[ProductAccessoryTypeEnum.SRG].length > 1) {
            inputs.push(
                new GtthReactiveNumber({
                    type: 'number',
                    key: this.SRG_QUANTIRY_ATTRIBUTE,
                    value: this.activeDevice.srgQuantity,
                    label: 'Surge Protector default quantity',
                    step: 1,
                    min: 0,
                    validationFn: [Validators.min(0), Validators.max(4)],
                    order: 3,
                    inputClass: this.formControllClass,
                    hideInput: !(
                        this.activeDevice.available[ProductAccessoryTypeEnum.SRG].length > 1 && this.historyService.isEditModeEnabled
                    )
                } as GtthReactiveNumber)
            );
        }

        if (
            !this.projectProductsService.isMh(this.activeDevice.type) &&
            !this.projectProductsService.isMhTgDevice(this.activeDevice.type)
        ) {
            inputs = inputs.concat([
                new GtthReactiveCheckbox({
                    key: this.configFieldsKeys.aes.id,
                    value: this.isChecked(this.activeDevice.aesId, this.activeDevice.available[ProductAccessoryTypeEnum.OPT_AES]),
                    label: 'AES',
                    order: 6,
                    checkboxLabel: 'Enable',
                    disabled: !this.historyService.isEditModeEnabled,
                    hideInput: !(
                        this.activeDevice.available[ProductAccessoryTypeEnum.OPT_AES].length > 1 && this.historyService.isEditModeEnabled
                    ),
                    displayValue: this.activeDevice.aes
                } as GtthReactiveCheckbox),
                new GtthReactiveCheckbox({
                    key: this.configFieldsKeys.l2.id,
                    value: this.isChecked(this.activeDevice.l2Id, this.activeDevice.available[ProductAccessoryTypeEnum.OPT_L2]),
                    label: 'L2',
                    order: 7,
                    checkboxLabel: 'Enable',
                    disabled: !this.historyService.isEditModeEnabled,
                    hideInput: !(
                        this.activeDevice.available[ProductAccessoryTypeEnum.OPT_L2].length > 1 && this.historyService.isEditModeEnabled
                    ),
                    displayValue: this.activeDevice.l2
                } as GtthReactiveCheckbox),
                new GtthReactiveCheckbox({
                    key: this.configFieldsKeys.extendmm.id,
                    value: this.isChecked(this.activeDevice.extendmmId, this.activeDevice.available[ProductAccessoryTypeEnum.OPT_EXTENDMM]),
                    label: 'ExtendMM',
                    order: 8,
                    checkboxLabel: 'Enable',
                    disabled: !this.historyService.isEditModeEnabled,
                    hideInput: !(
                        this.activeDevice.available[ProductAccessoryTypeEnum.OPT_EXTENDMM].length > 1 &&
                        this.historyService.isEditModeEnabled
                    ),
                    displayValue: this.activeDevice.extendmm
                } as GtthReactiveCheckbox),
                new GtthReactiveCheckbox({
                    key: this.configFieldsKeys.sync.id,
                    value: this.isChecked(this.activeDevice.syncId, this.activeDevice.available[ProductAccessoryTypeEnum.OPT_SYNC]),
                    label: 'Sync',
                    order: 9,
                    checkboxLabel: 'Enable',
                    disabled: !this.historyService.isEditModeEnabled,
                    hideInput: !(
                        this.activeDevice.available[ProductAccessoryTypeEnum.OPT_SYNC].length > 1 && this.historyService.isEditModeEnabled
                    ),
                    displayValue: this.activeDevice.sync
                } as GtthReactiveCheckbox)
            ]);
        }

        return inputs.sort((a, b) => a.order - b.order);
    }

    public closeConfigWindow() {
        this.closeConfig.emit();
    }

    private setActiveLayerIndex(layerIndex: number): void {
        this.activeLayerIndex = layerIndex;
        if (!this.historyService.logicalTopology.layersModeEnabled) {
            this.activeLayerIndex = -1;
        }
    }

    private initForm() {
        this.configInputs = this.getFormControls();
        this.editForm = this.inputControlService.toFormGroup(this.configInputs);
    }

    private isChecked(valueId: number, availableValues: GtthKeyValueDTO[]): boolean {
        if (availableValues && isDefined(availableValues[1])) {
            return valueId === availableValues[1].key;
        } else {
            return true;
        }
    }

    private loadDevicesConfigs() {
        this.buDevices = [];
        this.tuDevices = [];
        this.fxParts = new Map();
        this.devices = this.bomReportService.getDefaultBomDevices();
        this.devicesToShow = this.devices.filter(
            (deviceDto) => this.activeLayerIndex === -1 || deviceDto.layerIndex === this.activeLayerIndex
        );
        this.devices.forEach((device: ExtendedBomDeviceDTO) => {
            if (this.projectProductsService.isMh(device.type) || this.projectProductsService.isMhTgDevice(device.type)) {
                if (device.isSectorBu) {
                    this.buDevices.push(device);
                } else {
                    this.tuDevices.push(device);
                }
            } else if (device.deviceType !== ProductDeviceTypeEnum.NONE) {
                if (!this.fxParts.has(device.type)) {
                    const fxObject = {};
                    fxObject[ProductDeviceTypeEnum.FX_HIGHT] = [];
                    fxObject[ProductDeviceTypeEnum.FX_LOW] = [];
                    this.fxParts.set(device.type, fxObject);
                }

                this.fxParts.get(device.type)[device.deviceType].push(device);
            }
        });

        this.selectDevice(0);
    }

    private saveDeviceConfig() {
        if (this.editForm && this.editForm.valid && isDefined(this.activeDeviceIndex)) {
            this.parseBomFormToObj(this.devicesToShow[this.activeDeviceIndex], this.editForm);
        }

        this.bomReportService.setDefaultDevicesConfig(this.devices);
    }

    private parseBomFormToObj(device: ExtendedBomDeviceDTO, formData: FormGroup) {
        if (!device || !formData?.dirty) {
            return;
        }
        if (formData.controls) {
            // added custom case for srfQuantity parameter
            if (formData.controls[this.SRG_QUANTIRY_ATTRIBUTE]?.dirty) {
                let formValue = formData.controls[this.SRG_QUANTIRY_ATTRIBUTE].value;
                formValue = parseFloat(formValue);
                device[this.SRG_QUANTIRY_ATTRIBUTE] = formValue;
            }

            Object.keys(formData.controls).forEach((controlKey) => {
                if (!formData.controls[controlKey].dirty || !device[controlKey] || controlKey === this.SRG_QUANTIRY_ATTRIBUTE) {
                    return;
                }

                this.parseFormControlToObject(device, formData, controlKey);
            });
        }
    }

    private parseFormControlToObject(device: ExtendedBomDeviceDTO, formData: FormGroup, controlKey: string): void {
        let formValue = formData.controls[controlKey].value;
        let config;
        for (const key in this.configFieldsKeys) {
            if (this.configFieldsKeys[key].id === controlKey) {
                config = this.configFieldsKeys[key];
                break;
            }
        }

        const availableParams = device.available[config.type];
        let selectedValue;

        if (typeof formValue === 'boolean') {
            if (!availableParams || availableParams.length < 2) {
                return;
            }

            selectedValue = formValue ? availableParams[1] : availableParams[0];
        } else {
            if (!isNaN(formValue)) {
                formValue = parseFloat(formValue);
            }
            selectedValue = availableParams.find((value) => value.key === formValue);
        }

        if (
            device.deviceType !== ProductDeviceTypeEnum.NONE &&
            (controlKey === this.configFieldsKeys.l2.id ||
                controlKey === this.configFieldsKeys.extendmm.id ||
                controlKey === this.configFieldsKeys.sync.id ||
                controlKey === this.configFieldsKeys.aes.id ||
                controlKey === this.configFieldsKeys.capacityLicense.id)
        ) {
            this.setLicenseForNeighbourFx(device, config, selectedValue);
        }

        if (
            this.projectProductsService.isMh(device.type) ||
            (this.projectProductsService.isMhTgDevice(device.type) && controlKey === this.configFieldsKeys.capacityLicense.id)
        ) {
            this.setCapacityLicenseForMh(device, config, selectedValue);
        }

        device[config.id] = selectedValue.key;
        device[config.value] = selectedValue.value;
    }

    private setLicenseForNeighbourFx(device: ExtendedBomDeviceDTO, config: any, selectedValue: GtthKeyValueDTO) {
        const deviceIndex = this.fxParts.get(device.type)[device.deviceType].indexOf(device);
        let neighbout;
        if (device.deviceType === ProductDeviceTypeEnum.FX_HIGHT) {
            neighbout = this.fxParts.get(device.type)[ProductDeviceTypeEnum.FX_LOW][deviceIndex];
        } else {
            neighbout = this.fxParts.get(device.type)[ProductDeviceTypeEnum.FX_HIGHT][deviceIndex];
        }

        neighbout[config.id] = selectedValue.key;
        neighbout[config.value] = selectedValue.value;
    }

    private setCapacityLicenseForMh(device: ExtendedBomDeviceDTO, _config: any, selectedValue: GtthKeyValueDTO) {
        const available = device.available[ProductAccessoryTypeEnum.SOFTWARE_UPGRADE];

        if (available === undefined || available.length === 0) {
            return;
        }

        const selectedIndex = available.findIndex((value) => value.key === selectedValue.key);
        const prevSelectedIndex = available.findIndex((value) => value.key === device.capacityLicenseId);

        if (selectedIndex > prevSelectedIndex) {
            return;
        }

        if (device.isSectorBu) {
            this.setMHDeviceCapacityLicense(this.tuDevices, selectedIndex, device.layerIndex);
        } else {
            this.setMHDeviceCapacityLicense(this.buDevices, selectedIndex, device.layerIndex);
        }
    }

    private setMHDeviceCapacityLicense(devicesList: ExtendedBomDeviceDTO[], selectedIndex: number, layerIndex: number) {
        devicesList.forEach((device: ExtendedBomDeviceDTO) => {
            if (device.available[ProductAccessoryTypeEnum.SOFTWARE_UPGRADE][selectedIndex] && device.layerIndex === layerIndex) {
                device.capacityLicenseId = device.available[ProductAccessoryTypeEnum.SOFTWARE_UPGRADE][selectedIndex].key;
                device.capacityLicense = device.available[ProductAccessoryTypeEnum.SOFTWARE_UPGRADE][selectedIndex].value;
            }
        });
    }
}
